import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Container } from '../../assets/styles/GlobalStyle'
import { RootState } from '../../store'
import { AuthState } from '../../store/authSlice'

import { Form, Formik, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import api from '../../api'
import Responsive from '../../assets/config/Responsive'
import Colors from '../../assets/styles/Colors'
import Button from '../../components/Button'
import Input from '../../components/Input'
import Loader from '../../components/Loader'
import ScreenHeader from '../../components/ScreenHeader'

const Security = () => {
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  //
  const { t } = useTranslation()
  //
  // change password
  const [oldPassword, setOldPassword] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [phone, setPhone] = useState(auth.user?.phone || '')
  const [smsCode, setSmsCode] = useState<string>('')

  const navigate = useNavigate()
  const [disableForm, setDisableForm] = useState(false)

  // ############################################
  // update password mutation
  const [updatePassword, { isLoading: updatePasswordLoading }] = api.useUpdatePasswordMutation()
  // ############################################

  // send verification sms mutation
  const [verificationSms, { isLoading: isverificationSmsNumberLoading, isSuccess: verificationSmsNumberSuccess }] =
    api.useSendVerificationSmsMutation()
  //

  // send verification sms number
  const sendVerificationSmsFunction = async () => {
    if (phone?.length === 9) {
      try {
        //
        phone && (await verificationSms({ phone: phone }).unwrap())
        //
        toast.success(`${t('smsSuccessfullySent')}`)
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (sendVerificationSmsFunction)')
        }
      }
    }
  }
  //
  //

  // update password mutation
  const updatePasswordRequest = async () => {
    // e.preventDefault();
    const smsToNumber = parseInt(smsCode, 10)
    try {
      await updatePassword({
        oldPassword,
        password,
        passwordConfirm,
        smsCode: smsToNumber,
      }).unwrap()
      setDisableForm(true)
      toast.success('პაროლი წარმატებით განახლდა')
      setTimeout(() => {
        navigate('/my-policies')
      }, 2000)
    } catch (error: any) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0]
        const errorMessage = firstError.message
        toast.error(errorMessage)
      } else {
        toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (updatePasswordRequest)')
      }
    }
  }

  // yup validations schema
  const PersonalInfoValidationSchema = Yup.object({
    oldPassword: Yup.string(),
    password: Yup.string()
      .min(8, 'პაროლი უნდა შედგებოდეს 8 სიმბოლოსგან')
      .matches(/[0-9]/, 'პაროლი უნდა შეიცვდეს ციფრს')
      .matches(/[a-z]/, 'პაროლი უნდა შეიცავდეს პატარა ასოს')
      .matches(/[a-zA-Z]/, 'პაროლი უნდა შეიცავდეს ლათინურ ასოებს')
      .matches(/[A-Z]/, 'პაროლი უნდა შეიცავდეს დიდ ასოს'),
    // .matches(/[^\w]/, 'პაროლი უნდა შეიცავდეს სიმბოლოს'),
    passwordConfirm: Yup.string().oneOf([Yup.ref('password'), null], 'გთხოვთ შეიყვანოთ ერთნაირი პაროლი'),
    phone: Yup.string()
      .matches(/^\S*$/, 'გთხოვთ წაშალეთ გამოტოვება')
      .min(9, 'მობილურის ნომერი უნდა იყოს 9 ნიშნა')
      .max(9, 'მობილურის ნომერი უნდა იყოს 9 ნიშნა')
      .required('სავალდებულო'),
    smsCode: Yup.number().required('სავალდებულო'),
  })
  // initial values
  const initialValues = {
    oldPassword: oldPassword || '',
    password: password || '',
    passwordConfirm: passwordConfirm || '',
    phone: auth.user?.phone || '',
    smsCode: '',
  }
  // formi state
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: PersonalInfoValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false)
    },
  })
  //
  //

  const MainLoading = updatePasswordLoading

  return MainLoading ? (
    <Loader />
  ) : auth.user ? (
    <Formik
      initialValues={initialValues}
      validationSchema={PersonalInfoValidationSchema}
      enableReinitialize={true}
      validateOnMount={true}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(false)
      }}
    >
      {({ handleSubmit, handleBlur, setFieldValue, touched, errors, setTouched }) => (
        <Form onSubmit={handleSubmit}>
          <ScreenHeader text={t('myDashboard')} url="/my-policies" />

          <Container>
            <MainWrapper>
              <LargeTitle>{t('security')}</LargeTitle>

              <Wrapper>
                <CustomForm>
                  <Input
                    id="oldPassword"
                    label={t('currentPassword')}
                    type="password"
                    placeholder=""
                    value={oldPassword}
                    name="oldPassword"
                    icon={true}
                    onChange={(value) => [setOldPassword(value), setFieldValue('oldPassword', value)]}
                    error={(touched.oldPassword || oldPassword) && errors.oldPassword ? true : false}
                    errorText={errors.oldPassword}
                    onBlur={handleBlur}
                    width={'fullWidth'}
                    style={{ marginBottom: '24px' }}
                  />
                  <Input
                    id="newPassword"
                    label={t('newPassword')}
                    type="password"
                    placeholder=""
                    value={password}
                    name="password"
                    icon={true}
                    onChange={(value) => [setPassword(value), setFieldValue('password', value)]}
                    error={(touched.password || password) && errors.password ? true : false}
                    errorText={errors.password}
                    onBlur={handleBlur}
                    width={'fullWidth'}
                    style={{ marginBottom: '24px' }}
                  />
                  <Input
                    id="passwordConfirm"
                    label={t('repeatPassword')}
                    type="password"
                    placeholder=""
                    value={passwordConfirm}
                    name="passwordConfirm"
                    icon={true}
                    onChange={(value) => [setPasswordConfirm(value), setFieldValue('passwordConfirm', value)]}
                    error={(touched.passwordConfirm || passwordConfirm) && errors.passwordConfirm ? true : false}
                    errorText={errors.passwordConfirm}
                    onBlur={handleBlur}
                    width={'fullWidth'}
                    style={{ marginBottom: '24px' }}
                  />
                  <MobileInputDiv>
                    <Input
                      id="phone-number"
                      label={t('phone')}
                      type="email"
                      placeholder="555 000 000"
                      value={phone}
                      onChange={(value) => [setPhone(value), setFieldValue('phone', value)]}
                      width={'fullWidth'}
                      style={{ marginBottom: '24px' }}
                      disabled={isverificationSmsNumberLoading ? true : false}
                      error={(touched.phone || phone) && errors.phone ? true : false}
                      errorText={errors.phone}
                      name="phone"
                      onBlur={handleBlur}
                    />
                    {phone?.length === 9 && (
                      <SmsButton
                        disabled={
                          oldPassword &&
                          password &&
                          passwordConfirm &&
                          oldPassword.length >= 8 &&
                          password.length >= 8 &&
                          passwordConfirm.length >= 8 &&
                          password === passwordConfirm &&
                          !isverificationSmsNumberLoading
                            ? false
                            : true
                        }
                        onClick={sendVerificationSmsFunction}
                      >
                        {(verificationSmsNumberSuccess && t('Resend')) || t('sendSms')}
                      </SmsButton>
                    )}
                  </MobileInputDiv>
                  {verificationSmsNumberSuccess && phone && (
                    <Input
                      id="sms-code"
                      label={t('smsCode')}
                      type="number"
                      placeholder="1234"
                      value={smsCode}
                      onChange={(value) => [setSmsCode(value), setFieldValue('smsCode', value)]}
                      width={'fullWidth'}
                      style={{ marginBottom: '24px' }}
                      error={(touched.smsCode || smsCode) && errors.smsCode ? true : false}
                      errorText={errors.smsCode}
                      name="smsCode"
                      onBlur={handleBlur}
                    />
                  )}
                  <Button
                    onClick={() => {
                      setTouched({
                        oldPassword: true,
                        password: true,
                        passwordConfirm: true,
                        phone: true,
                        smsCode: true,
                      })

                      formik.validateForm().then((errors) => {
                        if (Object.keys(errors).length === 0) {
                          updatePasswordRequest()
                        }
                      })
                    }}
                    text={t('saveChanges')}
                    btnStyleType="primary"
                    fullWidth={true}
                    disabled={disableForm}
                  />
                  {/* ) : null} */}
                </CustomForm>
              </Wrapper>
            </MainWrapper>
          </Container>
        </Form>
      )}
    </Formik>
  ) : null
}

// styles
const SmsButton = styled.button`
  background-color: ${Colors.primaryRed};
  border: 0px;
  outline: none;
  padding: 3px 5px;
  font-size: 12px;
  position: absolute;
  right: 10px;
  top: 41px;
  padding: 10px;
  border-radius: 4px;
  color: ${Colors.white};
  ${Responsive.tablet} {
    padding: 7px;
    right: 7px;
    top: 41px;
  }
  ${Responsive.mobile} {
    padding: 7px;
    right: 7px;
    top: 41px;
  }

  &:disabled {
    opacity: 0.6;
  }
`
const Wrapper = styled.div`
  background-color: ${Colors.white};
  padding: 48px 64px 56px 64px;
  max-width: 528px;
  margin: 0 auto 140px auto;

  /* responsive */
  ${Responsive.mobile} {
    padding: 32px 24px 40px 24px;
    margin-bottom: 100px;
  }
`
const CustomForm = styled.div`
  margin-bottom: 25px;
`
const MainWrapper = styled.div`
  /*  */
`
const LargeTitle = styled.h1`
  font-size: 36px;
  line-height: 40px;
  color: ${Colors.primaryBlack};
  text-align: center;
  margin-top: 56px;
  margin-bottom: 56px;
  text-transform: capitalize;

  /* responsive */
  ${Responsive.tablet} {
    font-size: 24px;
    line-height: 26px;
  }

  /* responsive */
  ${Responsive.mobile} {
    font-size: 18px;
    line-height: 20px;
    margin-bottom: 56px;
    margin-top: 56px;
  }
`
const MobileInputDiv = styled.div`
  position: relative;
  /* inner button */
`
export default Security
